import { useEffect, useRef, useState } from "react";
import { VolumeX, PlayCircle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { assetLangFor } from "@/i18n/locale";
import styles from "./SplashHero.module.css";

interface SplashHeroProps {
  onComplete: () => void;
  onSkip?: () => void;
  forceShow?: boolean;
}

const SKIP_DELAY_MS = 3000;

export const SplashHero = ({ onComplete, onSkip }: SplashHeroProps) => {
  const { lang, t } = useLanguage();
  const assetLang = assetLangFor(lang);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [audioActivated, setAudioActivated] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);

  const videoSrc = `/splash/splash-${assetLang}.mp4`;
  const posterSrc = `/splash/splash-${assetLang}-poster.jpg`;

  // Reveal skip after a beat so it doesn't fight the opening frame.
  useEffect(() => {
    const id = window.setTimeout(() => setShowSkip(true), SKIP_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);

  // Warm up the auth bundle while the video plays so /auth feels instant.
  useEffect(() => {
    import("@/pages/Auth")
      .then(() => console.info("[SplashHero] Login preloaded"))
      .catch((err) => console.error("[SplashHero] Login preload failed:", err));
  }, []);

  const handleActivateAudio = () => {
    setAudioActivated(true);
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    // Some browsers pause the element when toggling muted post-autoplay; nudge
    // it forward so playback continues without a visible hitch.
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch((err) => {
        console.warn("[SplashHero] play() after unmute rejected:", err);
      });
    }
  };

  const handleEnded = () => {
    setVideoEnded(true);
  };

  const handleSkip = () => {
    if (onSkip) onSkip();
    else onComplete();
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    console.warn("[SplashHero] video failed to load — falling back to poster", {
      src: videoSrc,
      error: (e.target as HTMLVideoElement).error?.code ?? null,
    });
  };

  const handleContainerClick = () => {
    if (videoEnded) onComplete();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black overflow-hidden select-none"
      onClick={handleContainerClick}
      role="dialog"
      aria-modal="true"
      aria-label="Intro"
    >
      <video
        ref={videoRef}
        key={videoSrc}
        src={videoSrc}
        poster={posterSrc}
        playsInline
        preload="auto"
        autoPlay
        muted={!audioActivated}
        onEnded={handleEnded}
        onError={handleVideoError}
        // object-contain (not cover): on native the WebView is edge-to-edge, so
        // object-cover cropped the framing (hands out of frame). contain shows
        // the whole video letterboxed on the black container — no crop, no
        // distortion. On web the splash is full-screen too, so it just adds thin
        // black bars where aspect differs — acceptable.
        className="absolute inset-0 w-full h-full object-contain"
      />

      {!audioActivated && !videoEnded && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 pointer-events-none">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleActivateAudio();
            }}
            className={`pointer-events-auto relative h-[120px] w-[120px] rounded-full bg-white/10 backdrop-blur-md ring-2 ring-white/40 flex items-center justify-center text-white hover:bg-white/15 transition-colors ${styles.activateRing}`}
            aria-label={t("splash.tap_to_activate_voice")}
          >
            <VolumeX size={40} strokeWidth={1.5} />
          </button>
          <p className={`pointer-events-none text-white/85 text-sm tracking-wide ${styles.fadeIn}`}>
            {t("splash.tap_to_activate_voice")}
          </p>
        </div>
      )}

      {showSkip && !videoEnded && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleSkip();
          }}
          className={`absolute top-4 right-4 z-10 text-white/60 hover:text-white text-sm transition-colors ${styles.fadeIn}`}
        >
          {t("splash.skip")}
        </button>
      )}

      {videoEnded && (
        <div className={`absolute inset-x-0 bottom-12 z-10 flex flex-col items-center gap-2 ${styles.fadeIn}`}>
          <PlayCircle size={28} className="text-white/70" />
          <p className="text-white text-base tracking-wide">
            {t("splash.tap_to_enter")}
          </p>
        </div>
      )}
    </div>
  );
};

export default SplashHero;
